import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { googleReviews } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const GoogleReviews = () => {
  const sectionRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.reviews-content',
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const nextReview = () => {
    setCurrentIndex((prev) => (prev + 1) % googleReviews.length);
  };

  const prevReview = () => {
    setCurrentIndex((prev) => (prev - 1 + googleReviews.length) % googleReviews.length);
  };

  const visibleReviews = () => {
    const reviews = [];
    for (let i = 0; i < 3; i++) {
      reviews.push(googleReviews[(currentIndex + i) % googleReviews.length]);
    }
    return reviews;
  };

  return (
    <section ref={sectionRef} className="py-24 bg-securelec-light relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-securelec-red/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-securelec-red/5 rounded-full blur-[150px]" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="reviews-content inline-block text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
            Customer Reviews
          </span>
          <h2 className="reviews-content text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
            What Our <span className="text-securelec-red">Clients Say</span>
          </h2>
          
          {/* Google Rating */}
          <div className="reviews-content flex items-center justify-center gap-3 mb-4">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <span className="text-securelec-dark font-bold text-xl">5.0</span>
            <span className="text-securelec-gray/60">on Google</span>
          </div>
        </div>

        {/* Reviews Carousel */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={prevReview}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-20 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-securelec-red/10 hover:border-securelec-red/50 transition-all duration-300 shadow-md"
          >
            <ChevronLeft className="w-5 h-5 text-securelec-gray" />
          </button>
          <button
            onClick={nextReview}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-20 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-securelec-red/10 hover:border-securelec-red/50 transition-all duration-300 shadow-md"
          >
            <ChevronRight className="w-5 h-5 text-securelec-gray" />
          </button>

          {/* Reviews Grid */}
          <div className="reviews-content grid grid-cols-1 md:grid-cols-3 gap-6">
            {visibleReviews().map((review, index) => (
              <div
                key={`${review.id}-${index}`}
                className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-securelec-red/50 hover:shadow-xl transition-all duration-500 relative overflow-hidden"
              >
                {/* Quote Icon */}
                <Quote className="absolute top-4 right-4 w-8 h-8 text-securelec-red/10" />
                
                {/* Rating */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-securelec-gray/80 mb-6 leading-relaxed text-sm">
                  &ldquo;{review.text}&rdquo;
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-securelec-red/10 rounded-full flex items-center justify-center">
                    <span className="text-securelec-red font-bold">
                      {review.author.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <span className="text-securelec-dark font-semibold text-sm block">
                      {review.author}
                    </span>
                  </div>
                </div>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-securelec-red/0 to-securelec-red/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {googleReviews.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-securelec-red w-6'
                  : 'bg-securelec-gray/30 hover:bg-securelec-gray/50'
              }`}
            />
          ))}
        </div>

        {/* View on Google Button */}
        <div className="flex items-center justify-center mt-10">
          <a
            href="https://www.google.com/search?q=securelec&oq=securelec+&gs_lcrp=EgZjaHJvbWUyBggAEEUYOTIHCAEQABiABDIHCAIQABiABDIHCAMQABiABDIGCAQQRRg8MgYIBRBFGDwyBggGEEUYPDIGCAcQRRg80gEINzQ0MGowajeoAgCwAgA&sourceid=chrome&ie=UTF-8&zx=1773464841557&no_sw_cr=1#lrd=0x6ad673cb47f4dc17:0xb11aceb8375acef2,1,,,,"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-gray-200 hover:border-securelec-red/50 bg-white hover:bg-securelec-red/5 text-securelec-gray hover:text-securelec-red px-6 py-3 rounded-md font-semibold text-sm transition-all duration-300 shadow-sm hover:shadow-md"
            data-testid="view-on-google-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            View on Google
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
